/**
 * Toolbar for the Add/Edit Direct Delivery modal — search, the locked
 * supplier, date range, row filters, columns, route planning and the
 * container-fill readout.
 *
 * Split out of DirectDeliveryModal (CR-7717) to keep that component under the
 * 500-line limit; the markup is unchanged. Presentational only — every piece
 * of state stays owned by the modal and arrives as a prop.
 *
 * @link DirectDeliveryModal for the consumer.
 */
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Progress } from '../../ui/progress';
import { Route } from 'lucide-react';
import LocalColumnManager from './LocalColumnManager';
import type { LocalColumn } from './DirectDeliveryColumns';
import { useFilterVisibility } from '../../../hooks/useFilterVisibility';
import PartPrefixSelect from '../shared/PartPrefixSelect';
import type { PartPrefixFilter } from '../shared/partPrefixFilter';

interface DirectDeliveryToolbarProps {
  searchFilter: string;
  setSearchFilter: (value: string) => void;
  supplierName?: string;
  lockedFactoryCode: string;
  dateFrom: string;
  setDateFrom: (value: string) => void;
  dateTo: string;
  setDateTo: (value: string) => void;
  partFilter: PartPrefixFilter;
  setPartFilter: (filter: PartPrefixFilter) => void;
  commodityFilter: string;
  setCommodityFilter: (value: string) => void;
  commodityOptions: string[];
  columns: LocalColumn[];
  onToggleColumn: (id: string) => void;
  onMoveColumnUp: (id: string) => void;
  /** Customer's routing origin; the Plan Route button hides when blank. */
  routingOrigin: string;
  /** Postcodes of the selected orders, in selection order. */
  selectedPostcodes: string[];
  totalFillPercent: number;
  existingFillPercent: number;
  modalFillPercent: number;
  isEdit: boolean;
}

export default function DirectDeliveryToolbar({
  searchFilter, setSearchFilter, supplierName, lockedFactoryCode,
  dateFrom, setDateFrom, dateTo, setDateTo,
  partFilter, setPartFilter,
  commodityFilter, setCommodityFilter, commodityOptions,
  columns, onToggleColumn, onMoveColumnUp,
  routingOrigin, selectedPostcodes,
  totalFillPercent, existingFillPercent, modalFillPercent, isEdit,
}: DirectDeliveryToolbarProps) {
  // CR-4802: Commodity follows the tenant's declared filter shape.
  const fv = useFilterVisibility();
  return (
    <div className="flex items-end gap-4 px-6 py-3 border-b bg-muted/30 flex-wrap">
      <div className="space-y-1">
        <Label className="text-xs">Search</Label>
        <Input className="w-48 h-8" placeholder="SO#, Customer, Part..."
          value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Supplier</Label>
        <div
          className="w-44 h-8 text-sm border border-border rounded-lg px-2 bg-muted flex items-center font-medium"
          title="Locked to the call-off supplier"
        >
          {supplierName || 'Supplier'}
          {lockedFactoryCode && (
            <span className="ml-1 text-xs text-muted-foreground">({lockedFactoryCode})</span>
          )}
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Date From</Label>
        <Input type="date" className="w-36 h-8"
          value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Date To</Label>
        <Input type="date" className="w-36 h-8"
          value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Parts</Label>
        <PartPrefixSelect
          value={partFilter}
          onChange={setPartFilter}
          className="w-52 h-8 text-sm border border-border rounded-lg px-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      {fv.commodity && (
      <div className="space-y-1">
        <Label className="text-xs">Commodity Type</Label>
        <select
          value={commodityFilter}
          onChange={(e) => setCommodityFilter(e.target.value)}
          className="w-40 h-8 text-sm border border-border rounded-lg px-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Commodity type filter"
        >
          <option value="all">All commodity types</option>
          {commodityOptions.map(ct => (
            <option key={ct} value={ct}>{ct}</option>
          ))}
        </select>
      </div>
      )}
      <LocalColumnManager columns={columns} onToggle={onToggleColumn} onMoveUp={onMoveColumnUp} />
      {routingOrigin && selectedPostcodes.length > 0 && (
        <Button variant="outline" size="sm" className="gap-1.5"
          onClick={() => {
            const start = encodeURIComponent(routingOrigin);
            const waypoints = selectedPostcodes.map(pc => encodeURIComponent(pc)).join('/');
            window.open(`https://www.google.com/maps/dir/${start}/${waypoints}`, '_blank');
          }}>
          <Route className="h-3.5 w-3.5" />
          Plan Route
        </Button>
      )}
      <div className="flex-1" />
      <div className="text-right space-y-1">
        <Label className="text-xs">Container Fill</Label>
        <div className="flex items-center gap-2">
          <Progress value={Math.min(totalFillPercent, 100)} className="w-32 h-2" />
          <span className={`text-sm font-bold ${totalFillPercent > 90 ? 'text-red-600' : totalFillPercent > 50 ? 'text-orange-600' : 'text-green-600'}`}>
            {totalFillPercent}%
          </span>
        </div>
        {!isEdit && existingFillPercent > 0 && (
          <p className="text-xs text-muted-foreground">
            Existing: {existingFillPercent}% + This: {modalFillPercent}%
          </p>
        )}
      </div>
    </div>
  );
}
